import { useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import PageCard from "components/admin/PageCard";
import RecipientPicker from "layouts/mail/RecipientPicker";
import { useConfirm } from "components/admin/Feedback";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { formatNumber } from "utils/format";

/**
 * Tặng toàn bộ vật phẩm / pet để test, cho các nhân vật được chọn (thư riêng) hoặc mọi nhân vật
 * (thư toàn server). Máy chủ tự chia thành nhiều thư.
 */
function TestPackCard({ onSent }) {
  const confirm = useConfirm();
  const [run, busy] = useRunner();
  const info = useFetch(() => api.get("/api/admin/mail/test-pack"), []);
  const [target, setTarget] = useState("picked");
  const [recipients, setRecipients] = useState([]);
  const [form, setForm] = useState({
    items: true,
    pets: true,
    stackCount: 99,
    maxOption: true,
    expireDays: 7,
    includeNewPlayers: true,
  });

  const toggle = (key) => (event) => setForm({ ...form, [key]: event.target.checked });
  const set = (key) => (event) => setForm({ ...form, [key]: event.target.value });

  const data = info.data;
  const mails = data ? (form.items ? data.itemMails : 0) + (form.pets ? data.petMails : 0) : 0;

  const picked = target === "picked";

  const send = async () => {
    const parts = [];
    if (form.items) parts.push(`${formatNumber(data.items)} vật phẩm`);
    if (form.pets) parts.push(`${formatNumber(data.pets)} pet`);
    const who = picked
      ? recipients.map((r) => r.name || r.username).join(", ")
      : `MỌI nhân vật${form.includeNewPlayers ? " (kể cả nhân vật tạo sau)" : ""}`;
    const ok = await confirm({
      title: "Tặng all để test",
      message: `Tặng ${parts.join(" và ")} cho ${who}, chia thành ${mails} thư${
        picked ? " mỗi người" : " toàn server"
      }?`,
      color: "warning",
      confirmText: "Tặng all",
    });
    if (!ok) return;
    const res = await run(
      () =>
        api.post("/api/admin/mail/test-pack", {
          ...form,
          userIds: picked ? recipients.map((r) => r.userId) : null,
          includeNewPlayers: !picked && form.includeNewPlayers,
          stackCount: Number(form.stackCount),
          expireDays: Number(form.expireDays),
        }),
      (r) =>
        `Đã gửi ${formatNumber(r.lines)} món trong ${r.mails} thư tới ${formatNumber(
          r.players
        )} nhân vật${
          r.skipped?.length ? `, bỏ qua ${r.skipped.length} tài khoản chưa có nhân vật` : ""
        }`
    );
    if (res.ok) onSent();
  };

  const revoke = async () => {
    const ok = await confirm({
      title: "Thu hồi thư test",
      message:
        'Ẩn mọi thư toàn server và xoá mọi thư riêng chưa nhận có tiêu đề bắt đầu bằng "[Test] "? Ai đã nhận quà thì vẫn giữ.',
      color: "error",
      confirmText: "Thu hồi",
    });
    if (!ok) return;
    const res = await run(
      () => api.post("/api/admin/mail/test-pack/revoke"),
      (r) => `Đã thu hồi ${r.count} thư test`
    );
    if (res.ok) onSent();
  };

  return (
    <PageCard
      title="Tặng all để test"
      subtitle="Toàn bộ vật phẩm và pet cho nhân vật được chọn hoặc mọi nhân vật, nhận trong hòm thư"
      color="dark"
    >
      {info.error && <Alert severity="error">{info.error}</Alert>}
      <MDBox display="flex" flexDirection="column" gap={1.5}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={target}
          onChange={(e, value) => value && setTarget(value)}
        >
          <ToggleButton value="picked">Chọn nhân vật</ToggleButton>
          <ToggleButton value="all">Mọi nhân vật</ToggleButton>
        </ToggleButtonGroup>
        {picked && <RecipientPicker value={recipients} onChange={setRecipients} />}
        <MDBox display="flex" flexWrap="wrap" gap={2}>
          <FormControlLabel
            control={<Switch checked={form.items} onChange={toggle("items")} />}
            label={`Toàn bộ vật phẩm${data ? ` (${formatNumber(data.items)} món)` : ""}`}
          />
          <FormControlLabel
            control={<Switch checked={form.pets} onChange={toggle("pets")} />}
            label={`Toàn bộ pet vĩnh viễn${data ? ` (${formatNumber(data.pets)} pet)` : ""}`}
          />
        </MDBox>
        {form.items && (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <MDInput
                fullWidth
                type="number"
                label="Số lượng mỗi vật phẩm xếp chồng"
                value={form.stackCount}
                inputProps={{ min: 1, max: 9999 }}
                helperText={
                  data
                    ? `${formatNumber(
                        data.stackable
                      )} loại xếp chồng; trang bị, cánh, ngọc khảm mỗi loại 1 cái`
                    : null
                }
                onChange={set("stackCount")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch checked={form.maxOption} onChange={toggle("maxOption")} />}
                label={`Trang bị pet chỉ số tối đa${data ? ` (${data.equips} món)` : ""}`}
              />
            </Grid>
          </Grid>
        )}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <MDInput
              fullWidth
              type="number"
              label="Hết hạn sau (ngày, 0 = không)"
              value={form.expireDays}
              onChange={set("expireDays")}
            />
          </Grid>
          {!picked && (
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch checked={form.includeNewPlayers} onChange={toggle("includeNewPlayers")} />
                }
                label="Nhân vật tạo sau cũng nhận"
              />
            </Grid>
          )}
        </Grid>
        {data && (
          <MDTypography variant="caption" color="text">
            Chia thành {mails} thư{picked ? " cho mỗi nhân vật" : " toàn server"} (mỗi thư tối đa{" "}
            {data.linesPerMail} món, tiêu đề bắt đầu bằng &quot;[Test] &quot;). Trong game bấm
            &quot;Nhận tất cả&quot; ở hòm thư là nhận hết một lần.
          </MDTypography>
        )}
        <MDBox display="flex" gap={1} flexWrap="wrap">
          <MDButton
            variant="gradient"
            color="warning"
            disabled={
              busy || !data || (!form.items && !form.pets) || (picked && recipients.length === 0)
            }
            onClick={send}
          >
            <Icon>redeem</Icon>&nbsp;Tặng all
          </MDButton>
          <MDButton variant="outlined" color="error" disabled={busy} onClick={revoke}>
            <Icon>undo</Icon>&nbsp;Thu hồi thư test
          </MDButton>
        </MDBox>
      </MDBox>
    </PageCard>
  );
}

TestPackCard.propTypes = { onSent: PropTypes.func.isRequired };

export default TestPackCard;
